import { useState } from 'react';
import { ChartTable } from '../../components/charts/ChartTable.tsx';
import { SegmentedControl, SegmentedItem } from '../../components/ui/toggle-group.tsx';
import { cn } from '../../lib/cn.ts';
import { formatNumber, formatPercent, formatSeconds } from '../../lib/format.ts';
import { HEAT_STEPS, heatColor, topCells } from '../../lib/home.ts';
import { useLocale, useT } from '../../lib/i18n.tsx';
import type { CallPatternsResponse } from '../../lib/queries.ts';

type CellData = CallPatternsResponse['data']['heatmap'][number];
type Measure = 'calls' | 'answerRate' | 'wait';

const valueOf = (cell: CellData, measure: Measure): number | null =>
  measure === 'calls'
    ? cell.callsPerDay
    : measure === 'answerRate'
      ? cell.answerRate
      : cell.avgWaitSeconds;

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);

/**
 * Weekday × hour grid of calls. Calls are shown per day (a weekday that occurs five times in the
 * period is not "busier" than one that occurs four times); one hue from light to dark; the three
 * busiest slots are outlined and named under the grid. Hovering a cell explains it in words.
 */
export function CallHeatmap({ cells }: { cells: readonly CellData[] }) {
  const t = useT();
  const locale = useLocale();
  const hm = t.home.heatmap;
  const [measure, setMeasure] = useState<Measure>('calls');
  const [focus, setFocus] = useState<CellData | null>(null);

  const values = cells.map((c) => valueOf(c, measure));
  const max = Math.max(0, ...values.map((v) => v ?? 0));
  const busiest = topCells(
    cells.map((c) => ({ ...c, value: c.callsPerDay })),
    3,
  );
  const busiestKeys = new Set(busiest.map((c) => `${c.weekday}:${c.hour}`));
  const units = { seconds: t.units.seconds, minutes: t.units.minutes };

  const format = (value: number | null): string =>
    measure === 'calls'
      ? formatNumber(value, locale, 1)
      : measure === 'answerRate'
        ? formatPercent(value, locale, 0)
        : formatSeconds(value, locale, units);

  const describe = (cell: CellData): string =>
    [
      `${hm.weekdaysLong[cell.weekday] ?? ''}, ${t.home.hours.range(cell.hour)}`,
      hm.perDay(formatNumber(cell.callsPerDay, locale, 1)),
      cell.answerRate === null ? hm.tooFew : hm.answered(formatPercent(cell.answerRate, locale, 0)),
      cell.avgWaitSeconds === null
        ? null
        : hm.waitSeconds(formatSeconds(cell.avgWaitSeconds, locale, units)),
    ]
      .filter(Boolean)
      .join(' · ');

  return (
    <figure aria-label={hm.title} className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">{hm.subtitle}</p>
        <SegmentedControl
          aria-label={hm.metric}
          value={measure}
          onValueChange={(value) => {
            if (value) setMeasure(value as Measure);
          }}
        >
          <SegmentedItem value="calls">{hm.metrics.calls}</SegmentedItem>
          <SegmentedItem value="answerRate">{hm.metrics.answerRate}</SegmentedItem>
          <SegmentedItem value="wait">{hm.metrics.wait}</SegmentedItem>
        </SegmentedControl>
      </div>

      <div className="overflow-x-auto" onMouseLeave={() => setFocus(null)}>
        <div
          className="grid min-w-[640px] grid-cols-[2.25rem_repeat(24,minmax(0,1fr))] gap-0.5"
          aria-hidden="true"
          data-testid="call-heatmap"
        >
          <span />
          {HOURS.map((hour) => (
            <span
              key={hour}
              className="pb-1 text-center text-[11px] text-muted-foreground tabular-nums"
            >
              {hour}
            </span>
          ))}
          {hm.weekdays.map((day, weekday) => (
            <Row key={day} label={day}>
              {HOURS.map((hour) => {
                const cell = cells[weekday * 24 + hour];
                if (!cell) return <span key={hour} />;
                const value = valueOf(cell, measure);
                const color = heatColor(value, max);
                const top = busiestKeys.has(`${weekday}:${hour}`);
                return (
                  <span
                    key={hour}
                    title={describe(cell)}
                    onMouseEnter={() => setFocus(cell)}
                    className={cn(
                      'h-7 rounded-[3px]',
                      color === null &&
                        'bg-[repeating-linear-gradient(45deg,var(--muted),var(--muted)_3px,var(--card)_3px,var(--card)_6px)]',
                      top && 'ring-2 ring-foreground ring-offset-1 ring-offset-card',
                      focus === cell && 'outline-2 outline-ring',
                    )}
                    style={color ? { background: color } : undefined}
                  />
                );
              })}
            </Row>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
        <p className="min-h-4" aria-live="polite">
          {focus
            ? describe(focus)
            : busiest.length > 0
              ? hm.busiest(
                  busiest
                    .map((c) => `${hm.weekdays[c.weekday] ?? ''} ${t.home.hours.range(c.hour)}`)
                    .join(', '),
                )
              : null}
        </p>
        <span className="inline-flex items-center gap-1.5">
          {hm.low}
          <span className="flex" aria-hidden="true">
            {HEAT_STEPS.map((step) => (
              <span
                key={step}
                className="h-2.5 w-4 first:rounded-l-sm last:rounded-r-sm"
                style={{ background: step }}
              />
            ))}
          </span>
          {hm.high}
          {max > 0 ? <span className="tabular-nums">({format(max)})</span> : null}
        </span>
      </div>

      <ChartTable
        title={hm.title}
        columns={['', ...hm.weekdays]}
        rows={HOURS.map((hour) => [
          t.home.hours.range(hour),
          ...hm.weekdays.map((_, weekday) => {
            const cell = cells[weekday * 24 + hour];
            return cell ? format(valueOf(cell, measure)) : '';
          }),
        ])}
      />
    </figure>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <span className="flex items-center text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </>
  );
}
